// src/views/hospitality/BeRoomAllocation.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  Radio,
  Tag,
  Button,
  Switch,
  Table,
  Select as AntSelect,
  InputNumber,
  Tooltip,
  Input,
  message,
  Spin,
  Alert,
  Modal,
  Skeleton,
  Badge,
} from "antd";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import {
  Code as ApiOutlined,
  RefreshCw as CloudSyncOutlined,
  Info as InfoCircleOutlined,
  Grid as DashboardOutlined,
  Zap as ThunderboltOutlined,
  Copy as CopyOutlined,
  RefreshCw as ReloadOutlined,
  CheckCircle as CheckCircleTwoTone,
  XCircle as CloseCircleTwoTone,
  Save as SaveOutlined,
  RotateCcw as UndoOutlined,
  AlertCircle as ExclamationCircleOutlined,
  Key as KeyOutlined,
} from "react-feather";
import moment from "moment";
import axios from "axios";

const { Option } = AntSelect;
const { confirm } = Modal;

const DEFAULT_SYNC_MODE = "stayx";

const defaultIntegrationState = () => ({
  syncMode: DEFAULT_SYNC_MODE,

  stayXSettings: {
    syncInventory: true,
    syncRates: false,
    syncRestrictions: false,
    masterSource: "pms", // pms | be
  },

  thirdPartySettings: {
    webhookUrl: "",
    token: "",
    allowBookings: true,
    allowCancellations: true,

    vendorPushUrl: "",
    pushBookingsEnabled: false,
  },
});

const normalizeSyncMode = (v) => {
  const m = String(v || "").toLowerCase();
  return ["stayx", "thirdparty", "manual"].includes(m) ? m : DEFAULT_SYNC_MODE;
};

const normalizeAllocationMode = (v) => {
  const m = String(v || "").toLowerCase();
  return ["full", "fixed", "percentage"].includes(m) ? m : "full";
};

const safeInt = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const BeRoomAllocation = () => {
  const apiUrl =
    process.env.REACT_APP_API_URL || "http://localhost:3000/api/hms";
  const token = localStorage.getItem("token");

  const authHeaders = useMemo(
    () => ({
      headers: { Authorization: token ? `Bearer ${token}` : "" },
    }),
    [token]
  );

  const today = moment().format("DD MMM YYYY, HH:mm");

  // --- Loading / Error / Dirty UX ---
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [softInit, setSoftInit] = useState(false); // indicates "first-time" config
  const [dirty, setDirty] = useState(false);
  const [loadWarning, setLoadWarning] = useState(""); // non-blocking banner copy
  const lastServerSnapshotRef = useRef(null);

  // --- Room types from BE (real data) ---
  const [beRoomTypes, setBeRoomTypes] = useState([]); // raw list
  const beRoomTypesById = useMemo(() => {
    const map = new Map();
    (beRoomTypes || []).forEach((rt) => {
      const id = rt.room_type_id ?? rt.id;
      if (!id) return;
      map.set(Number(id), rt);
    });
    return map;
  }, [beRoomTypes]);

  // --- UI state (integration + table) ---
  const [syncMode, setSyncMode] = useState(DEFAULT_SYNC_MODE);

  const [stayXSettings, setStayXSettings] = useState({
    syncInventory: true,
    syncRates: false,
    syncRestrictions: false,
    masterSource: "pms",
  });

  const [thirdPartySettings, setThirdPartySettings] = useState({
    webhookUrl: "",
    token: "",
    allowBookings: true,
    allowCancellations: true,
    vendorPushUrl: "",
    pushBookingsEnabled: false,
  });

  // --- Allocation rows ---
  // shape preserved:
  // { key, beRoomTypeId, beRoomType, pmsRoomType, allocationMode, allotment, percentage, overbookingAllowed, active }
  const [rows, setRows] = useState([]);

  const ensureAuth = () => {
    if (!token) {
      message.error("You are not logged in.");
      return false;
    }
    return true;
  };

  // --- Create default rows from BE room types ---
  const buildDefaultRowsFromRoomTypes = (roomTypesRaw) => {
    const list = Array.isArray(roomTypesRaw) ? roomTypesRaw : [];
    return list.map((rt, idx) => ({
      key: rt.room_type_id ?? rt.id ?? idx + 1,
      beRoomTypeId: Number(rt.room_type_id ?? rt.id),
      beRoomType: rt.type_name ?? rt.name ?? "Room Type",
      pmsRoomType: null, // external_room_type_code (ONLY meaningful for 3rd-party)
      allocationMode: "full",
      allotment: 0,
      percentage: 0,
      overbookingAllowed: false,
      active: true,
    }));
  };

  const markDirty = () => setDirty(true);

  const setFromSnapshot = (snapshot) => {
    if (!snapshot) return;

    setSyncMode(snapshot.syncMode);
    setStayXSettings(snapshot.stayXSettings);
    setThirdPartySettings(snapshot.thirdPartySettings);
    setRows(snapshot.rows);

    lastServerSnapshotRef.current = snapshot;
    setDirty(false);
  };

  // SweetAlert toast (enterprise style)
  const swalToast = useMemo(
    () =>
      Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3500,
        timerProgressBar: true,
      }),
    []
  );

  const fetchAll = async () => {
    if (!ensureAuth()) return;

    setLoading(true);
    setSoftInit(false);
    setLoadWarning("");

    // helpers
    const unwrapData = (res) => {
      if (!res) return null;
      const d = res.data;
      if (!d) return null;
      return d.data !== undefined ? d.data : d;
    };

    const isNotConfigured = (err) => {
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      const msg = String(err?.response?.data?.message || "").toLowerCase();

      if (status === 404) return true;
      if (code === "NOT_CONFIGURED") return true;
      if (
        msg.includes("not configured") ||
        msg.includes("no settings") ||
        msg.includes("not found")
      )
        return true;
      return false;
    };

    const toResult = (resOrErr) => {
      if (resOrErr?.__err)
        return { ok: false, err: resOrErr.__err, data: null };
      return { ok: true, err: null, data: unwrapData(resOrErr) };
    };

    try {
      const [integrationRaw, allocationRaw, roomTypesRes] = await Promise.all([
        axios
          .get(`${apiUrl}/booking-engine-settings/integration`, authHeaders)
          .catch((e) => ({ __err: e })),

        axios
          .get(`${apiUrl}/booking-engine-settings/room-allocation`, authHeaders)
          .catch((e) => ({ __err: e })),

        // Room types are REQUIRED.
        axios
          .get(`${apiUrl}/roomtype/listings/`, authHeaders)
          .catch((e) => ({ __err: e })),
      ]);

      const integration = toResult(integrationRaw);
      const allocation = toResult(allocationRaw);
      const roomTypes = toResult(roomTypesRes);

      // --- Room types (required) ---
      if (!roomTypes.ok) {
        await Swal.fire({
          icon: "error",
          title: "Unable to load room types",
          text: "We couldn’t load room types required for allocation. Please try again.",
          confirmButtonText: "Retry",
        });

        const defaults = defaultIntegrationState();
        setFromSnapshot({
          syncMode: defaults.syncMode,
          stayXSettings: defaults.stayXSettings,
          thirdPartySettings: defaults.thirdPartySettings,
          rows: [],
        });
        setSoftInit(true);
        return;
      }

      const rtRaw =
        roomTypes.data?.roomTypes ||
        roomTypes.data?.items ||
        roomTypes.data?.data ||
        roomTypes.data ||
        [];

      const rtArr = Array.isArray(rtRaw) ? rtRaw : [];
      setBeRoomTypes(rtArr);

      const defaults = defaultIntegrationState();

      const snap = {
        syncMode: defaults.syncMode,
        stayXSettings: defaults.stayXSettings,
        thirdPartySettings: defaults.thirdPartySettings,
        rows: buildDefaultRowsFromRoomTypes(rtArr),
      };

      // UX flags
      let shouldSoftInit = false;
      let showWarning = false;

      // --- Integration (optional) ---
      if (!integration.ok) {
        if (isNotConfigured(integration.err)) {
          shouldSoftInit = true;
        } else {
          showWarning = true;
        }
      } else if (integration.data) {
        const integrationData = integration.data;

        snap.syncMode = normalizeSyncMode(integrationData.sync_mode);

        snap.stayXSettings = {
          syncInventory:
            integrationData.stayx_sync_inventory === undefined
              ? true
              : !!integrationData.stayx_sync_inventory,
          syncRates:
            integrationData.stayx_sync_rates === undefined
              ? false
              : !!integrationData.stayx_sync_rates,
          syncRestrictions:
            integrationData.stayx_sync_restrictions === undefined
              ? false
              : !!integrationData.stayx_sync_restrictions,
          masterSource:
            String(integrationData.stayx_master_source || "pms").toLowerCase() ===
            "be"
              ? "be"
              : "pms",
        };

        snap.thirdPartySettings = {
          webhookUrl: integrationData.inbound_webhook_url || "",
          token: integrationData.inbound_hmac_secret
            ? `********************${String(
                integrationData.inbound_hmac_secret
              ).slice(-4)}`
            : "",
          allowBookings:
            integrationData.inbound_allow_bookings === undefined
              ? true
              : !!integrationData.inbound_allow_bookings,
          allowCancellations:
            integrationData.inbound_allow_cancellations === undefined
              ? true
              : !!integrationData.inbound_allow_cancellations,
          vendorPushUrl: integrationData.outbound_push_url || "",
          pushBookingsEnabled: !!integrationData.outbound_push_enabled,
        };
      } else {
        shouldSoftInit = true;
      }

      // --- Allocation (optional) ---
      let allocationArr = [];
      if (!allocation.ok) {
        if (isNotConfigured(allocation.err)) {
          shouldSoftInit = true;
        } else {
          showWarning = true;
        }
      } else {
        allocationArr = Array.isArray(allocation.data)
          ? allocation.data
          : allocation.data?.items || [];
        if (!allocationArr.length) shouldSoftInit = true;
      }

      if (allocationArr.length) {
        const allocByRoomId = new Map();
        allocationArr.forEach((r) => {
          const id = Number(r.be_room_type_id);
          if (id) allocByRoomId.set(id, r);
        });

        snap.rows = buildDefaultRowsFromRoomTypes(rtArr).map((row) => {
          const rule = allocByRoomId.get(row.beRoomTypeId);
          if (!rule) return row;

          return {
            ...row,
            // External mapping only meaningful for thirdparty; we still hydrate to preserve data if saved.
            pmsRoomType: rule.external_room_type_code || null,
            allocationMode: normalizeAllocationMode(rule.allocation_mode),
            allotment: safeInt(rule.fixed_allotment, 0),
            percentage: safeInt(rule.percent_allotment, 0),
            overbookingAllowed: !!rule.overbooking_allowed,
            active: rule.is_active === undefined ? true : !!rule.is_active,
          };
        });
      }

      // Apply once
      setFromSnapshot(snap);

      if (shouldSoftInit) setSoftInit(true);

      if (showWarning && !shouldSoftInit) {
        setLoadWarning(
          "We couldn’t load saved settings right now. Showing safe defaults — you can retry or save again."
        );
        swalToast.fire({ icon: "warning", title: "Showing safe defaults" });
      }
    } catch (err) {
      console.error(err);

      await Swal.fire({
        icon: "error",
        title: "Something went wrong",
        text: "We couldn’t load the Room Allocation page. Please try again.",
        confirmButtonText: "OK",
      });

      const defaults = defaultIntegrationState();
      setFromSnapshot({
        syncMode: defaults.syncMode,
        stayXSettings: defaults.stayXSettings,
        thirdPartySettings: defaults.thirdPartySettings,
        rows: [],
      });
      setSoftInit(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateRow = (key, patch) => {
    setRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, ...patch } : row))
    );
    markDirty();
  };

  const handleDiscard = () => {
    const snap = lastServerSnapshotRef.current;
    if (!snap) return;

    if (!dirty) {
      message.info("No changes to discard.");
      return;
    }

    confirm({
      title: "Discard changes?",
      icon: <ExclamationCircleOutlined />,
      content:
        "Your unsaved changes will be lost. This action cannot be undone.",
      okText: "Discard",
      okButtonProps: { danger: true },
      cancelText: "Keep editing",
      onOk: () => {
        setFromSnapshot(snap);
        message.info("Changes discarded.");
      },
    });
  };

  const buildIntegrationPayload = () => {
    return {
      sync_mode: normalizeSyncMode(syncMode),

      stayx_sync_inventory: !!stayXSettings.syncInventory,
      stayx_sync_rates: !!stayXSettings.syncRates,
      stayx_sync_restrictions: !!stayXSettings.syncRestrictions,
      stayx_master_source: stayXSettings.masterSource === "be" ? "be" : "pms",

      inbound_allow_bookings: !!thirdPartySettings.allowBookings,
      inbound_allow_cancellations: !!thirdPartySettings.allowCancellations,

      outbound_push_url: thirdPartySettings.vendorPushUrl?.trim() || null,
      outbound_push_enabled: !!thirdPartySettings.pushBookingsEnabled,
    };
  };

  const buildAllocationPayload = () => {
    const items = (rows || []).map((r) => {
      // ✅ StayX & Manual: external mapping not required. We persist null to avoid confusion.
      const externalCode =
        syncMode === "thirdparty" ? r.pmsRoomType || null : null;

      return {
        be_room_type_id: Number(r.beRoomTypeId),
        be_room_type_name: r.beRoomType || "",
        external_room_type_code: externalCode,
        external_room_type_name: null,
        allocation_mode: normalizeAllocationMode(r.allocationMode),
        fixed_allotment: safeInt(r.allotment, 0),
        percent_allotment: safeInt(r.percentage, 0),
        overbooking_allowed: !!r.overbookingAllowed,
        is_active: r.active === undefined ? true : !!r.active,
      };
    });

    return { items };
  };

  const validateBeforeSave = () => {
    // ✅ Only 3rd-party requires external mapping
    if (syncMode === "thirdparty") {
      const missingMap = rows.some((r) => r.active && !r.pmsRoomType);
      if (missingMap) {
        message.warning(
          "Some active room types are not mapped to your vendor room codes. You can save now, but mapping is recommended."
        );
      }

      if (
        thirdPartySettings.pushBookingsEnabled &&
        !String(thirdPartySettings.vendorPushUrl || "").trim()
      ) {
        message.error("Please enter Vendor Push URL or disable outbound push.");
        return false;
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!ensureAuth()) return;
    if (!validateBeforeSave()) return;

    setSaving(true);
    try {
      await Promise.all([
        axios.post(
          `${apiUrl}/booking-engine-settings/integration`,
          buildIntegrationPayload(),
          authHeaders
        ),
        axios.post(
          `${apiUrl}/booking-engine-settings/room-allocation`,
          buildAllocationPayload(),
          authHeaders
        ),
      ]);

      message.success("Room allocation & sync settings saved.");
      await fetchAll();
    } catch (err) {
      console.error(err);
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save settings."
      );
    } finally {
      setSaving(false);
    }
  };

  const rotateSecret = async () => {
    if (!ensureAuth()) return;

    confirm({
      title: "Regenerate shared secret?",
      icon: <KeyOutlined />,
      content:
        "This will rotate the inbound secret token. Your vendor must update their configuration immediately to avoid failed webhooks.",
      okText: "Regenerate",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setSaving(true);
          await axios.post(
            `${apiUrl}/booking-engine-settings/integration/rotate-secret`,
            {},
            authHeaders
          );
          message.success("Secret rotated successfully.");
          await fetchAll();
        } catch (err) {
          console.error(err);
          message.error("Failed to rotate secret.");
        } finally {
          setSaving(false);
        }
      },
    });
  };


  const sendTestPing = async () => {
  if (!ensureAuth()) return;

  try {
    setSaving(true);

    const res = await axios.post(
      `${apiUrl}/booking-engine-settings/integration/test-ping`,
      {},
      authHeaders
    );

    message.success(res?.data?.message || "Test ping sent successfully.");
    await fetchAll();
  } catch (err) {
    console.error(err);
    message.error(
      err?.response?.data?.message ||
        err?.message ||
        "Failed to send test ping."
    );
  } finally {
    setSaving(false);
  }
};
  /* ---- derived preview for bottom bar ---- */
  const activeRows = useMemo(
    () => (rows || []).filter((r) => r.active),
    [rows]
  );

  const todayAllotmentEstimate = useMemo(() => {
    return activeRows.reduce((sum, r) => {
      const rt = beRoomTypesById.get(Number(r.beRoomTypeId));
      const physical = safeInt(rt?.total_rooms, 0);

      if (syncMode === "manual") {
        return sum + Math.max(physical, 0);
      }

      if (r.allocationMode === "fixed") return sum + safeInt(r.allotment, 0);

      if (r.allocationMode === "percentage") {
        const pct = Math.min(100, Math.max(0, safeInt(r.percentage, 0)));
        if (!physical) return sum;
        return sum + Math.round((physical * pct) / 100);
      }

      return sum + Math.max(physical, 0);
    }, 0);
  }, [activeRows, beRoomTypesById, syncMode]);

  /* ---- Table columns ---- */
  const columns = useMemo(
    () => [
      {
        title: "Booking Engine Room Type",
        dataIndex: "beRoomType",
        key: "beRoomType",
        fixed: "left",
        width: 290,
        render: (text, r) => {
          const rt = beRoomTypesById.get(Number(r.beRoomTypeId));
          const total = rt?.total_rooms != null ? String(rt.total_rooms) : null;

          return (
            <div style={{ minWidth: 260 }}>
              <div style={{ fontWeight: 800 }}>{text}</div>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                Room Type ID: <b>{r.beRoomTypeId}</b>
                {total && (
                  <>
                    {" "}
                    · Total Rooms: <b>{total}</b>
                  </>
                )}
              </div>
            </div>
          );
        },
      },
      {
        title: "External Room Type Code",
        dataIndex: "pmsRoomType",
        key: "pmsRoomType",
        width: 260,
        render: (value, record) => {
          // ✅ NEW BEHAVIOR:
          // StayX = internal mapping, no external code
          if (syncMode === "stayx") {
            return (
              <div>
                <Tag color="green">Auto-mapped (StayX)</Tag>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                  Uses the same NetShine room type ID — no vendor codes required.
                </div>
              </div>
            );
          }

          if (syncMode === "manual") {
            return <Tag color="blue">Managed in Manual Calendar</Tag>;
          }

          // thirdparty only
          return (
            <div>
              <Input
                value={value || ""}
                placeholder="e.g., DLX / STD_KING / RM01"
                onChange={(e) =>
                  updateRow(record.key, { pmsRoomType: e.target.value || null })
                }
              />
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                Enter the room code used in your PMS / channel manager.
              </div>
            </div>
          );
        },
      },
      {
        title: "Allocation Mode",
        dataIndex: "allocationMode",
        key: "allocationMode",
        width: 230,
        render: (value, record) =>
          syncMode === "manual" ? (
            <Tag>Manual calendar</Tag>
          ) : (
            <AntSelect
              style={{ width: "100%" }}
              value={value}
              onChange={(v) => updateRow(record.key, { allocationMode: v })}
            >
              <Option value="full">Full inventory</Option>
              <Option value="fixed">Fixed allotment</Option>
              <Option value="percentage">Percentage of rooms</Option>
            </AntSelect>
          ),
      },
      {
        title: "Allotment / %",
        dataIndex: "allotment",
        key: "allotment",
        width: 210,
        render: (_, record) => {
          if (syncMode === "manual") {
            return <span className="text-muted">Controlled by calendar</span>;
          }
          if (record.allocationMode === "fixed") {
            return (
              <InputNumber
                min={0}
                max={999}
                value={record.allotment}
                onChange={(val) =>
                  updateRow(record.key, { allotment: val || 0 })
                }
              />
            );
          }
          if (record.allocationMode === "percentage") {
            return (
              <InputNumber
                min={0}
                max={100}
                formatter={(v) => `${v || 0}%`}
                parser={(v) => Number(String(v).replace("%", ""))}
                value={record.percentage}
                onChange={(val) =>
                  updateRow(record.key, { percentage: val || 0 })
                }
              />
            );
          }
          return <span className="text-muted">All rooms</span>;
        },
      },
      {
        title: (
          <>
            Overbooking{" "}
            <Tooltip title="Allow Booking Engine to sell more rooms than allocated. Use with caution.">
              <InfoCircleOutlined />
            </Tooltip>
          </>
        ),
        dataIndex: "overbookingAllowed",
        key: "overbookingAllowed",
        width: 160,
        align: "center",
        render: (value, record) =>
          syncMode === "manual" ? (
            <Tag color="default">N/A</Tag>
          ) : (
            <Switch
              checked={!!value}
              onChange={(checked) =>
                updateRow(record.key, { overbookingAllowed: checked })
              }
            />
          ),
      },
      {
        title: "Status",
        dataIndex: "active",
        key: "active",
        width: 150,
        align: "center",
        render: (value, record) => (
          <span
            style={{ cursor: "pointer" }}
            onClick={() => updateRow(record.key, { active: !value })}
          >
            {value ? (
              <>
                <CheckCircleTwoTone twoToneColor="#52c41a" />{" "}
                <span style={{ color: "#52c41a" }}>Active</span>
              </>
            ) : (
              <>
                <CloseCircleTwoTone twoToneColor="#ff4d4f" />{" "}
                <span style={{ color: "#ff4d4f" }}>Paused</span>
              </>
            )}
          </span>
        ),
      },
    ],
    [beRoomTypesById, syncMode]
  );

  const headerRightTag = useMemo(() => {
    if (saving) return <Tag color="gold">SAVING…</Tag>;
    if (dirty) return <Tag color="orange">UNSAVED CHANGES</Tag>;
    return <Tag color="blue">LIVE CONFIG</Tag>;
  }, [dirty, saving]);

  const syncModeMeta = useMemo(() => {
    const meta = {
      stayx: {
        label: "NetShine Stay X PMS",
        short: "StayX",
        description:
          "Internal PMS sync with automatic room-type mapping and controlled inventory/rate flow.",
        color: "blue",
        icon: <DashboardOutlined />,
      },
      thirdparty: {
        label: "3rd-Party PMS / Channel",
        short: "3rd-party",
        description:
          "Map room types to external vendor codes and exchange signed webhooks with your PMS or channel provider.",
        color: "purple",
        icon: <ApiOutlined />,
      },
      manual: {
        label: "Manual Booking Engine Only",
        short: "Manual",
        description:
          "Manage inventory directly inside NetShine Booking Engine without PMS or channel-manager sync.",
        color: "orange",
        icon: <ThunderboltOutlined />,
      },
    };

    return meta[syncMode] || meta.stayx;
  }, [syncMode]);

  const activeRoomTypeCount = activeRows.length;
  const totalRoomTypeCount = rows.length;
  const inactiveRoomTypeCount = Math.max(totalRoomTypeCount - activeRoomTypeCount, 0);
  const mappedThirdPartyRows = useMemo(
    () => rows.filter((row) => row.active && row.pmsRoomType).length,
    [rows]
  );

  const activeCoverage = totalRoomTypeCount
    ? Math.round((activeRoomTypeCount / totalRoomTypeCount) * 100)
    : 0;

  const saveStatusText = saving
    ? "Saving configuration"
    : dirty
    ? "Unsaved changes"
    : softInit
    ? "Ready for first save"
    : "Configuration is up to date";

  const switchPill = (label, checked) => (
    <span className={`ns-bealloc-mini-pill ${checked ? "on" : "off"}`}>
      <span />
      {label}
    </span>
  );

  const ModeOption = ({ mode, icon, title, description, tag }) => (
    <button
      type="button"
      className={`ns-bealloc-mode-card ${syncMode === mode ? "active" : ""}`}
      onClick={() => {
        setSyncMode(mode);
        markDirty();
      }}
    >
      <div className="ns-bealloc-mode-icon">{icon}</div>
      <div className="ns-bealloc-mode-content">
        <div className="ns-bealloc-mode-title-row">
          <strong>{title}</strong>
          {tag ? <Tag color={syncMode === mode ? "blue" : "default"}>{tag}</Tag> : null}
        </div>
        <span>{description}</span>
      </div>
    </button>
  );

  const SectionTitle = ({ icon, eyebrow, title, description }) => (
    <div className="ns-bealloc-section-head">
      <div className="ns-bealloc-section-title">
        <span>{icon}</span>
        <div>
          <small>{eyebrow}</small>
          <strong>{title}</strong>
          {description ? <p>{description}</p> : null}
        </div>
      </div>
      <Button size="small" icon={<ReloadOutlined />} onClick={fetchAll} loading={loading}>
        Refresh
      </Button>
    </div>
  );

  if (loading) {
    return (
      <div className="page-wrapper-full">
        <style>{`
          .ns-bealloc-loading-screen {
            min-height: calc(100vh - 70px);
            display: flex;
            align-items: center;
            justify-content: center;
            background:
              radial-gradient(circle at 18% 8%, rgba(37, 99, 235, 0.10), transparent 28%),
              radial-gradient(circle at 82% 0%, rgba(14, 165, 233, 0.10), transparent 26%),
              #f8fafc;
          }
          .ns-bealloc-loading-card {
            background: rgba(255,255,255,0.94);
            border: 1px solid rgba(15, 23, 42, 0.08);
            border-radius: 22px;
            box-shadow: 0 18px 50px rgba(15, 23, 42, 0.10);
            padding: 28px 34px;
            min-width: 360px;
          }
          .ns-bealloc-loading-title {
            color: #001f5b;
            font-weight: 900;
            font-size: 1.05rem;
            margin-top: 14px;
          }
          .ns-bealloc-loading-copy {
            color: #64748b;
            margin-top: 4px;
            font-size: 0.86rem;
          }
        `}</style>
        <div className="ns-bealloc-loading-screen">
          <div className="ns-bealloc-loading-card text-center">
            <Spin size="large" />
            <div className="ns-bealloc-loading-title">Loading Room Allocation &amp; Sync</div>
            <div className="ns-bealloc-loading-copy">
              Fetching integration settings, allocation rules, and room types.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper-full">
      <style>{`
        .ns-bealloc-page {
          min-height: calc(100vh - 70px);
          background:
            radial-gradient(circle at 12% 4%, rgba(37, 99, 235, 0.08), transparent 28%),
            radial-gradient(circle at 82% 0%, rgba(14, 165, 233, 0.10), transparent 26%),
            #f8fafc;
          padding-bottom: 88px;
        }

        .ns-bealloc-hero {
          position: relative;
          overflow: hidden;
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.19), transparent 34%),
            radial-gradient(circle at top right, rgba(16, 185, 129, 0.11), transparent 30%),
            linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 24px;
          box-shadow: 0 18px 48px rgba(15, 23, 42, 0.08);
          padding: 22px;
          margin-bottom: 16px;
        }

        .ns-bealloc-hero::after {
          content: "";
          position: absolute;
          right: -80px;
          top: -80px;
          width: 220px;
          height: 220px;
          border-radius: 999px;
          background: rgba(37, 99, 235, 0.08);
          pointer-events: none;
        }

        .ns-bealloc-hero-inner {
          position: relative;
          z-index: 1;
          display: flex;
          justify-content: space-between;
          gap: 18px;
          flex-wrap: wrap;
          align-items: flex-start;
        }

        .ns-bealloc-eyebrow {
          color: #2563eb;
          font-size: 0.74rem;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          font-weight: 900;
          margin-bottom: 7px;
        }

        .ns-bealloc-title-row {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .ns-bealloc-title {
          color: #001f5b;
          font-size: 1.58rem;
          line-height: 1.15;
          font-weight: 900;
          margin: 0;
        }

        .ns-bealloc-live-pill {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          border-radius: 999px;
          background: #eff6ff;
          color: #1d4ed8;
          border: 1px solid #bfdbfe;
          padding: 5px 10px;
          font-size: 0.72rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          font-weight: 900;
        }

        .ns-bealloc-live-dot {
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: #2563eb;
          box-shadow: 0 0 0 5px rgba(37, 99, 235, 0.12);
        }

        .ns-bealloc-subtitle {
          color: #64748b;
          font-size: 0.9rem;
          line-height: 1.6;
          margin: 9px 0 0;
          max-width: 790px;
        }

        .ns-bealloc-hero-actions {
          display: flex;
          gap: 8px;
          align-items: center;
          justify-content: flex-end;
          flex-wrap: wrap;
        }

        .ns-bealloc-status-pill {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          border-radius: 999px;
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          color: #334155;
          padding: 8px 12px;
          font-size: 0.78rem;
          font-weight: 900;
          box-shadow: 0 8px 20px rgba(15, 23, 42, 0.05);
          white-space: nowrap;
        }

        .ns-bealloc-kpi-row {
          margin-bottom: 16px;
        }

        .ns-bealloc-kpi {
          border: 0 !important;
          border-radius: 20px !important;
          box-shadow: 0 12px 34px rgba(15, 23, 42, 0.08);
          overflow: hidden;
          height: 100%;
        }

        .ns-bealloc-kpi-inner {
          min-height: 142px;
          padding: 16px;
          border: 1px solid rgba(15, 23, 42, 0.07);
          border-radius: 20px;
          background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .ns-bealloc-kpi-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 10px;
        }

        .ns-bealloc-kpi-icon {
          width: 42px;
          height: 42px;
          border-radius: 15px;
          background: #eff6ff;
          color: #1d4ed8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 1.08rem;
        }

        .ns-bealloc-kpi-label {
          color: #64748b;
          font-size: 0.74rem;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          margin-bottom: 4px;
        }

        .ns-bealloc-kpi-value {
          color: #0f172a;
          font-size: 1.65rem;
          line-height: 1.1;
          font-weight: 900;
        }

        .ns-bealloc-kpi-note {
          color: #64748b;
          font-size: 0.78rem;
          line-height: 1.45;
          margin-top: 8px;
        }

        .ns-bealloc-panel {
          border: 0 !important;
          border-radius: 22px !important;
          box-shadow: 0 14px 38px rgba(15, 23, 42, 0.08);
          overflow: hidden;
          margin-bottom: 16px;
        }

        .ns-bealloc-panel .ant-card-body {
          padding: 18px !important;
        }

        .ns-bealloc-section-head {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          align-items: flex-start;
          flex-wrap: wrap;
          padding-bottom: 14px;
          margin-bottom: 16px;
          border-bottom: 1px solid rgba(15, 23, 42, 0.08);
        }

        .ns-bealloc-section-title {
          display: flex;
          gap: 12px;
          align-items: flex-start;
        }

        .ns-bealloc-section-title > span {
          width: 42px;
          height: 42px;
          border-radius: 15px;
          background: #eff6ff;
          color: #1d4ed8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 1.1rem;
          flex: 0 0 auto;
        }

        .ns-bealloc-section-title small {
          display: block;
          color: #2563eb;
          font-size: 0.68rem;
          font-weight: 900;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          margin-bottom: 4px;
        }

        .ns-bealloc-section-title strong {
          display: block;
          color: #0f172a;
          font-size: 1rem;
          font-weight: 900;
          line-height: 1.2;
        }

        .ns-bealloc-section-title p {
          color: #64748b;
          font-size: 0.82rem;
          line-height: 1.5;
          margin: 5px 0 0;
        }

        .ns-bealloc-mode-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 12px;
        }

        .ns-bealloc-mode-card {
          position: relative;
          border: 1px solid rgba(15, 23, 42, 0.08);
          background: #ffffff;
          border-radius: 18px;
          padding: 14px;
          display: flex;
          gap: 12px;
          align-items: flex-start;
          text-align: left;
          min-height: 132px;
          transition: all 0.18s ease;
          cursor: pointer;
        }

        .ns-bealloc-mode-card:hover,
        .ns-bealloc-mode-card.active {
          border-color: rgba(37, 99, 235, 0.35);
          box-shadow: 0 12px 28px rgba(37, 99, 235, 0.10);
          transform: translateY(-1px);
        }

        .ns-bealloc-mode-card.active {
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.12), transparent 32%),
            #ffffff;
        }

        .ns-bealloc-mode-icon {
          width: 40px;
          height: 40px;
          border-radius: 15px;
          background: #eff6ff;
          color: #1d4ed8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
        }

        .ns-bealloc-mode-title-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 8px;
          margin-bottom: 5px;
        }

        .ns-bealloc-mode-title-row strong {
          color: #0f172a;
          font-weight: 900;
          line-height: 1.25;
        }

        .ns-bealloc-mode-content span {
          display: block;
          color: #64748b;
          font-size: 0.8rem;
          line-height: 1.5;
        }

        .ns-bealloc-setting-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 14px;
        }

        .ns-bealloc-setting-card {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 18px;
          padding: 15px;
          min-height: 160px;
        }

        .ns-bealloc-setting-title {
          color: #0f172a;
          font-weight: 900;
          margin-bottom: 6px;
        }

        .ns-bealloc-muted {
          color: #64748b;
          font-size: 0.8rem;
          line-height: 1.5;
        }

        .ns-bealloc-switch-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 9px 0;
          border-bottom: 1px dashed rgba(15, 23, 42, 0.10);
          color: #334155;
          font-weight: 800;
        }

        .ns-bealloc-switch-row:last-child {
          border-bottom: none;
        }

        .ns-bealloc-code-box {
          background: #f8fafc;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 14px;
          padding: 10px 11px;
          color: #0f172a;
          font-size: 0.78rem;
          word-break: break-all;
          line-height: 1.45;
          min-height: 42px;
        }

        .ns-bealloc-mini-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: #475569;
          font-size: 0.76rem;
          font-weight: 900;
          margin-right: 8px;
          margin-top: 8px;
        }

        .ns-bealloc-mini-pill span {
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: #94a3b8;
        }

        .ns-bealloc-mini-pill.on span {
          background: #16a34a;
          box-shadow: 0 0 0 4px rgba(22, 163, 74, 0.12);
        }

        .ns-bealloc-table-card .ant-card-head {
          border-bottom: 1px solid rgba(15, 23, 42, 0.08);
          background:
            radial-gradient(circle at top right, rgba(37, 99, 235, 0.07), transparent 26%),
            #ffffff;
        }

        .ns-bealloc-table-card .ant-card-head-title {
          color: #0f172a;
          font-weight: 900;
        }

        .ns-bealloc-table .ant-table {
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
        }

        .ns-bealloc-table .ant-table-thead > tr > th {
          background: #f8fafc !important;
          color: #0f172a;
          font-weight: 900;
        }

        .ns-bealloc-table .ant-table-tbody > tr:hover > td {
          background: #f8fbff !important;
        }

        .ns-bealloc-table .ant-input,
        .ns-bealloc-table .ant-input-number,
        .ns-bealloc-table .ant-select-selector {
          border-radius: 12px !important;
        }

        .ns-bealloc-bottom-bar {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 1000;
          pointer-events: none;
          padding: 0 18px 14px;
        }

        .ns-bealloc-bottom-inner {
          pointer-events: auto;
          max-width: 1180px;
          margin: 0 auto;
          background: rgba(255, 255, 255, 0.94);
          border: 1px solid rgba(15, 23, 42, 0.10);
          border-radius: 20px;
          box-shadow: 0 -14px 42px rgba(15, 23, 42, 0.12);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          padding: 13px 15px;
        }

        .ns-bealloc-preview-title {
          color: #0f172a;
          font-weight: 900;
          margin-bottom: 2px;
        }

        .ns-bealloc-preview-copy {
          color: #64748b;
          font-size: 0.8rem;
          line-height: 1.45;
        }

        .ns-bealloc-saving-overlay {
          position: fixed;
          inset: 0;
          background: rgba(248, 250, 252, 0.48);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(5px);
          -webkit-backdrop-filter: blur(5px);
        }

        .ns-bealloc-saving-card {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 20px;
          box-shadow: 0 18px 50px rgba(15, 23, 42, 0.12);
          padding: 24px 30px;
          text-align: center;
          min-width: 320px;
        }

        .ns-bealloc-saving-card strong {
          display: block;
          color: #0f172a;
          margin-top: 12px;
          font-size: 1rem;
        }

        .ns-bealloc-saving-card span {
          display: block;
          color: #64748b;
          margin-top: 4px;
          font-size: 0.84rem;
        }

        @media (max-width: 992px) {
          .ns-bealloc-mode-grid,
          .ns-bealloc-setting-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .ns-bealloc-page {
            padding-bottom: 150px;
          }
          .ns-bealloc-hero {
            padding: 16px;
          }
          .ns-bealloc-title {
            font-size: 1.28rem;
          }
          .ns-bealloc-hero-actions {
            width: 100%;
            justify-content: flex-start;
          }
          .ns-bealloc-bottom-bar {
            padding: 0 10px 10px;
          }
        }
      `}</style>

      <div className="content container-fluid ns-bealloc-page">
        <div className="ns-bealloc-hero">
          <div className="ns-bealloc-hero-inner">
            <div>
              <div className="ns-bealloc-eyebrow">Booking Engine Inventory Control</div>
              <div className="ns-bealloc-title-row">
                <h3 className="ns-bealloc-title">Room Allocation &amp; Sync</h3>
                <span className="ns-bealloc-live-pill">
                  <span className="ns-bealloc-live-dot" />
                  {syncModeMeta.short} Mode
                </span>
              </div>
              <p className="ns-bealloc-subtitle">
                Decide how inventory flows between NetShine Booking Engine, StayX PMS, manual availability, and external PMS or channel providers. Review mappings, allocation rules, overbooking controls, and webhook settings from one command center.
              </p>
            </div>

            <div className="ns-bealloc-hero-actions">
              <span className="ns-bealloc-status-pill">{saveStatusText}</span>
              <span className="ns-bealloc-status-pill">Last viewed: {today}</span>
              {headerRightTag}
            </div>
          </div>
        </div>

        <Row gutter={[16, 16]} className="ns-bealloc-kpi-row">
          <Col xs={24} sm={12} xl={6}>
            <Card className="ns-bealloc-kpi" bodyStyle={{ padding: 0 }}>
              <div className="ns-bealloc-kpi-inner">
                <div className="ns-bealloc-kpi-top">
                  <div>
                    <div className="ns-bealloc-kpi-label">Room Types</div>
                    <div className="ns-bealloc-kpi-value">{totalRoomTypeCount}</div>
                  </div>
                  <div className="ns-bealloc-kpi-icon"><DashboardOutlined /></div>
                </div>
                <div className="ns-bealloc-kpi-note">{activeRoomTypeCount} active and {inactiveRoomTypeCount} paused.</div>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} xl={6}>
            <Card className="ns-bealloc-kpi" bodyStyle={{ padding: 0 }}>
              <div className="ns-bealloc-kpi-inner">
                <div className="ns-bealloc-kpi-top">
                  <div>
                    <div className="ns-bealloc-kpi-label">Sellable Today</div>
                    <div className="ns-bealloc-kpi-value">{todayAllotmentEstimate}</div>
                  </div>
                  <div className="ns-bealloc-kpi-icon"><ThunderboltOutlined /></div>
                </div>
                <div className="ns-bealloc-kpi-note">Estimated room inventory exposed by active allocation rules.</div>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} xl={6}>
            <Card className="ns-bealloc-kpi" bodyStyle={{ padding: 0 }}>
              <div className="ns-bealloc-kpi-inner">
                <div className="ns-bealloc-kpi-top">
                  <div>
                    <div className="ns-bealloc-kpi-label">Active Coverage</div>
                    <div className="ns-bealloc-kpi-value">{activeCoverage}%</div>
                  </div>
                  <div className="ns-bealloc-kpi-icon"><CheckCircleTwoTone twoToneColor="#16a34a" /></div>
                </div>
                <div className="ns-bealloc-kpi-note">Share of room types currently active for allocation.</div>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} xl={6}>
            <Card className="ns-bealloc-kpi" bodyStyle={{ padding: 0 }}>
              <div className="ns-bealloc-kpi-inner">
                <div className="ns-bealloc-kpi-top">
                  <div>
                    <div className="ns-bealloc-kpi-label">External Mapped</div>
                    <div className="ns-bealloc-kpi-value">{syncMode === "thirdparty" ? mappedThirdPartyRows : "—"}</div>
                  </div>
                  <div className="ns-bealloc-kpi-icon"><ApiOutlined /></div>
                </div>
                <div className="ns-bealloc-kpi-note">
                  {syncMode === "thirdparty" ? "Active rows with vendor room codes." : "Only required for 3rd-party sync mode."}
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        {!!loadWarning && (
          <Alert
            type="warning"
            showIcon
            style={{ borderRadius: 16, marginBottom: 14 }}
            message="Some settings could not be loaded"
            description={<div style={{ color: "#475569" }}>{loadWarning}</div>}
          />
        )}

        {softInit && (
          <Alert
            type="info"
            showIcon
            style={{ borderRadius: 16, marginBottom: 14 }}
            message="Welcome — let’s set up your Booking Engine sync"
            description={
              <div style={{ color: "#475569" }}>
                We didn’t find an existing configuration for your property yet. We loaded smart defaults using your current room types. Review the sync mode, mappings, and allocation rules, then click <b>Save Allocation Settings</b>.
              </div>
            }
          />
        )}

        <Card className="ns-bealloc-panel">
          <SectionTitle
            icon={<CloudSyncOutlined />}
            eyebrow="Inventory Source"
            title="Choose sync mode"
            description="Select the operating mode for Booking Engine inventory and reservation synchronization."
          />

          <div className="ns-bealloc-mode-grid">
            <ModeOption
              mode="stayx"
              icon={<DashboardOutlined />}
              title="NetShine Stay X PMS"
              tag="Recommended"
              description="Automatic room-type mapping using internal NetShine room type IDs. Ideal when StayX is the property operating layer."
            />
            <ModeOption
              mode="thirdparty"
              icon={<ApiOutlined />}
              title="3rd-Party PMS / Channel"
              tag="Vendor Sync"
              description="Map each Booking Engine room type to an external vendor code and exchange signed booking webhooks."
            />
            <ModeOption
              mode="manual"
              icon={<ThunderboltOutlined />}
              title="Manual BE Only"
              tag="Standalone"
              description="Use the Booking Engine availability calendar without PMS/channel sync. Best for quick direct booking setup."
            />
          </div>
        </Card>

        <Card className="ns-bealloc-panel">
          {syncMode === "stayx" && (
            <>
              <SectionTitle
                icon={<CloudSyncOutlined />}
                eyebrow="StayX Integration"
                title="Internal PMS synchronization"
                description="Room type mapping is automatic in StayX mode. You can control inventory, rate, and restriction sync below."
              />

              <div className="ns-bealloc-setting-grid">
                <div className="ns-bealloc-setting-card">
                  <div className="ns-bealloc-setting-title">Connection</div>
                  <div className="ns-bealloc-muted">Connected PMS Property</div>
                  <div style={{ color: "#0f172a", fontWeight: 900, marginTop: 5 }}>
                    {localStorage.getItem("property_name") || "Your Property"}
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <Tag color="green">Connected</Tag>
                  </div>
                  <div className="ns-bealloc-muted" style={{ marginTop: 10 }}>
                    Last inventory sync: {today}
                  </div>
                </div>

                <div className="ns-bealloc-setting-card">
                  <div className="ns-bealloc-setting-title">Sync Scope</div>
                  <div className="ns-bealloc-switch-row">
                    <span>Sync Inventory</span>
                    <Switch
                      checked={stayXSettings.syncInventory}
                      onChange={(checked) => {
                        setStayXSettings((s) => ({ ...s, syncInventory: checked }));
                        markDirty();
                      }}
                    />
                  </div>
                  <div className="ns-bealloc-switch-row">
                    <span>Sync Rates</span>
                    <Switch
                      checked={stayXSettings.syncRates}
                      onChange={(checked) => {
                        setStayXSettings((s) => ({ ...s, syncRates: checked }));
                        markDirty();
                      }}
                    />
                  </div>
                  <div className="ns-bealloc-switch-row">
                    <span>Sync Restrictions</span>
                    <Switch
                      checked={stayXSettings.syncRestrictions}
                      onChange={(checked) => {
                        setStayXSettings((s) => ({ ...s, syncRestrictions: checked }));
                        markDirty();
                      }}
                    />
                  </div>
                </div>

                <div className="ns-bealloc-setting-card">
                  <div className="ns-bealloc-setting-title">Master Source</div>
                  <Radio.Group
                    value={stayXSettings.masterSource}
                    onChange={(e) => {
                      setStayXSettings((s) => ({ ...s, masterSource: e.target.value }));
                      markDirty();
                    }}
                  >
                    <div style={{ marginBottom: 8 }}><Radio value="pms">Stay X PMS is master</Radio></div>
                    <div><Radio value="be">Booking Engine can override rates</Radio></div>
                  </Radio.Group>
                  <div className="ns-bealloc-muted" style={{ marginTop: 12 }}>
                    Bookings and cancellations from BE can be written back to PMS in real time depending on your integration contract.
                  </div>
                </div>
              </div>
            </>
          )}

          {syncMode === "thirdparty" && (
            <>
              <SectionTitle
                icon={<ApiOutlined />}
                eyebrow="3rd-Party Sync"
                title="External PMS / Channel integration"
                description="Configure inbound webhooks, HMAC secret, outbound push, and vendor room-type mapping."
              />

              <div className="ns-bealloc-setting-grid">
                <div className="ns-bealloc-setting-card">
                  <div className="ns-bealloc-setting-title">Inbound Booking Webhook</div>
                  <div className="ns-bealloc-muted">Your PMS / Channel calls NetShine.</div>
                  <div className="d-flex align-items-center gap-2" style={{ marginTop: 10 }}>
                    <div className="ns-bealloc-code-box" style={{ flex: 1 }}>
                      {thirdPartySettings.webhookUrl || "Not set yet — save once to generate default."}
                    </div>
                    <Button
                      type="text"
                      icon={<CopyOutlined />}
                      onClick={() => {
                        if (!thirdPartySettings.webhookUrl) {
                          return message.warning("Webhook URL is not available yet.");
                        }
                        navigator.clipboard.writeText(thirdPartySettings.webhookUrl);
                        message.success("Webhook URL copied.");
                      }}
                    />
                  </div>
                  <div className="ns-bealloc-muted" style={{ marginTop: 10 }}>
                    Share this URL with your PMS / Channel provider for incoming bookings and cancellations.
                  </div>
                </div>

                <div className="ns-bealloc-setting-card">
                  <div className="ns-bealloc-setting-title">Shared Secret &amp; Inbound Rules</div>
                  <div className="d-flex align-items-center gap-2">
                    <div className="ns-bealloc-code-box" style={{ flex: 1 }}>
                      {thirdPartySettings.token || "Not generated yet"}
                    </div>
                    <Button size="small" icon={<KeyOutlined />} onClick={rotateSecret}>Regenerate</Button>
                  </div>
                  <div className="ns-bealloc-switch-row" style={{ marginTop: 10 }}>
                    <span>Accept inbound bookings</span>
                    <Switch
                      checked={thirdPartySettings.allowBookings}
                      onChange={(checked) => {
                        setThirdPartySettings((s) => ({ ...s, allowBookings: checked }));
                        markDirty();
                      }}
                    />
                  </div>
                  <div className="ns-bealloc-switch-row">
                    <span>Accept cancellations/updates</span>
                    <Switch
                      checked={thirdPartySettings.allowCancellations}
                      onChange={(checked) => {
                        setThirdPartySettings((s) => ({ ...s, allowCancellations: checked }));
                        markDirty();
                      }}
                    />
                  </div>
                  <Button icon={<CloudSyncOutlined />} onClick={sendTestPing} loading={saving} style={{ marginTop: 10 }}>
                    Send test ping
                  </Button>
                </div>

                <div className="ns-bealloc-setting-card">
                  <div className="ns-bealloc-setting-title">
                    Outbound Booking Push URL{" "}
                    <Tooltip title="When enabled, NetShine will POST every confirmed booking / cancellation to this URL as a signed webhook.">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </div>
                  <div className="ns-bealloc-muted">NetShine calls your PMS / Channel.</div>
                  <Input
                    style={{ marginTop: 10, borderRadius: 12 }}
                    value={thirdPartySettings.vendorPushUrl}
                    onChange={(e) => {
                      setThirdPartySettings((s) => ({ ...s, vendorPushUrl: e.target.value }));
                      markDirty();
                    }}
                    placeholder="https://vendor-pms.example.com/netshine/bookings"
                  />
                  <div className="ns-bealloc-switch-row" style={{ marginTop: 10 }}>
                    <span>Push bookings &amp; cancellations</span>
                    <Switch
                      checked={thirdPartySettings.pushBookingsEnabled}
                      onChange={(checked) => {
                        setThirdPartySettings((s) => ({ ...s, pushBookingsEnabled: checked }));
                        markDirty();
                      }}
                    />
                  </div>
                  <div className="ns-bealloc-muted" style={{ marginTop: 10 }}>
                    Events include <code>booking.created</code> and <code>booking.cancelled</code> with HMAC-SHA256 signing.
                  </div>
                </div>
              </div>
            </>
          )}

          {syncMode === "manual" && (
            <>
              <SectionTitle
                icon={<ThunderboltOutlined />}
                eyebrow="Manual Inventory"
                title="Booking Engine only mode"
                description="No PMS or channel manager is connected. Inventory and restrictions are managed inside NetShine Booking Engine."
              />

              <div className="ns-bealloc-setting-grid">
                <div className="ns-bealloc-setting-card">
                  <div className="ns-bealloc-setting-title">Manual Control</div>
                  <div className="ns-bealloc-muted">
                    Availability and restrictions come from the Manual Inventory Calendar. Rate plans can still be reused.
                  </div>
                </div>
                <div className="ns-bealloc-setting-card">
                  <div className="ns-bealloc-setting-title">Recommended For</div>
                  <div className="ns-bealloc-muted">
                    Properties starting direct booking quickly before connecting PMS or channel manager sync.
                  </div>
                </div>
                <div className="ns-bealloc-setting-card">
                  <div className="ns-bealloc-setting-title">Next Step</div>
                  <Link
                    to={`/booking-engine/manual-inventory?return=${encodeURIComponent("/booking-engine/room-allocation")}`}
                    className="btn add-btn custom-btn"
                  >
                    Go to Availability Calendar
                  </Link>
                </div>
              </div>
            </>
          )}
        </Card>

        <Card
          className="ns-bealloc-panel ns-bealloc-table-card"
          title={
            <div>
              <div>Room Type Mapping &amp; Allocation Rules</div>
              <div style={{ color: "#64748b", fontSize: 12, fontWeight: 500, marginTop: 3 }}>
                Control which room types are sellable and how much inventory should be exposed to Booking Engine.
              </div>
            </div>
          }
          extra={
            <span className="text-muted small">
              Active room types: <b>{activeRows.length}</b>
              {dirty && <Tag color="orange" className="ms-2">Pending save</Tag>}
            </span>
          }
        >
          <Table
            className="ns-bealloc-table"
            columns={columns}
            dataSource={rows}
            pagination={false}
            rowKey="key"
            scroll={{ x: 1100 }}
            size="middle"
            locale={{
              emptyText: (
                <div style={{ padding: 24 }}>
                  <div style={{ fontWeight: 900, fontSize: 16, color: "#0f172a" }}>No room types found</div>
                  <div style={{ color: "#6b7280", marginTop: 6 }}>
                    Please ensure room types exist in PMS or Booking Engine room types endpoint.
                  </div>
                  <Button className="mt-3" icon={<ReloadOutlined />} onClick={fetchAll}>
                    Reload
                  </Button>
                </div>
              ),
            }}
          />
        </Card>
      </div>

      <div className="ns-bealloc-bottom-bar">
        <div className="ns-bealloc-bottom-inner">
          <Row gutter={[16, 16]} align="middle">
            <Col md={14} xs={24}>
              <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                <div>
                  <div className="ns-bealloc-preview-title">Live allocation preview</div>
                  <div className="ns-bealloc-preview-copy">
                    Estimated sellable rooms today: <b>{todayAllotmentEstimate}</b> across <b>{activeRows.length}</b> active room types.
                  </div>
                </div>
                <div>
                  {saving ? (
                    <Badge status="processing" text="Saving…" />
                  ) : dirty ? (
                    <Badge status="warning" text="Unsaved changes" />
                  ) : (
                    <Badge status="success" text="Up to date" />
                  )}
                </div>
              </div>
            </Col>

            <Col md={10} xs={24} className="text-md-end">
              <Button
                className="me-2"
                icon={<UndoOutlined />}
                onClick={handleDiscard}
                disabled={!dirty || saving}
              >
                Discard Changes
              </Button>

              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saving}
              >
                Save Allocation Settings
              </Button>
            </Col>
          </Row>
        </div>
      </div>

      {saving && (
        <div className="ns-bealloc-saving-overlay">
          <div className="ns-bealloc-saving-card">
            <Spin />
            <strong>Saving your configuration…</strong>
            <span>Please don’t close this tab.</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BeRoomAllocation;
